<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { ApiResultOfLoginDTO, LoginWalletDTO } from "@frontend/walletapi";
    import { Startup } from "@frontend/startup";
    import { Login, me, RedirectAfterLogin } from "@frontend/user";
    import { translate } from "@frontend/Translator";

    // svelte-ignore unused-export-let
    export let params: any = {};

    let pin: string = "";
    let identifier: string = "";
    let loginResult: ApiResultOfLoginDTO | undefined = undefined;

    let turnstileToken = '';
    let turnstileWidgetId: string | null = null;
    let turnstileLoaded = false;
    let errorMessage = '';
    let isSubmitting = false;

    const unsubscribe = me.subscribe((value) => {
        RedirectAfterLogin(value)
    });
    
    
    async function handleSubmit() 
    {
        // Verificar que el captcha se completó
        if (!verifyTurnstile()) {
            return;
        }

        isSubmitting = true;
        errorMessage = '';

        try {
            const loginData: LoginWalletDTO = {
                email: identifier.includes('@') ? identifier : undefined,
                PhoneNumber: identifier.includes('@') ? undefined : identifier,
                password: pin,
                TurnstileToken: turnstileToken
            };
            
            loginResult = await Login(loginData);
            pin = "";
            
            if (!loginResult?.Success)
            {
                errorMessage = loginResult?.ErrorMessage || $translate('Login error. Please try again.');
                resetTurnstile();
                return;
            }

            unsubscribe?.();
            await Startup(false);
            RedirectAfterLogin(loginResult?.Data?.User);
        } catch (error) {
            console.error('Error during login:', error);
            errorMessage = $translate('Connection error. Please try again.');
            resetTurnstile();
        } finally {
            isSubmitting = false;
        }
    }
    

    onMount(() => {
        // Inicializar Turnstile cuando el componente se monta
        if (turnstileLoaded) {
            renderTurnstile();
        }
    });

    onDestroy(() => {
        unsubscribe?.();
        // Limpiar el widget de Turnstile al destruir el componente
        if (turnstileWidgetId) {
            (window as any).turnstile?.remove(turnstileWidgetId);
        }
    })


     // Función callback para cuando se completa el captcha
    function onTurnstileSuccess(token: string) {
        turnstileToken = token;
    }

    // Función callback para cuando hay error en el captcha
    function onTurnstileError() {
        errorMessage = $translate('Captcha verification error. Please try again.');
        turnstileToken = '';
    }

    // Función para verificar que el captcha se completó
    function verifyTurnstile(): boolean {
        if (!turnstileToken) {
            errorMessage = $translate('Please complete the captcha verification.');
            return false;
        }
        return true;
    }

     // Función global para cargar Turnstile
    if (typeof window !== 'undefined') {
        (window as any).onTurnstileLoad = () => {
            turnstileLoaded = true;
            setTimeout(() => {
                renderTurnstile();
            }, 100);
        };
    }

        // Función para renderizar el widget de Turnstile
    function renderTurnstile() {
        // Solo renderizar si no hay un widget ya creado
        if (turnstileWidgetId) {
            return;
        }

        if (typeof window !== 'undefined' && (window as any).turnstile && turnstileLoaded) {
            const container = document.getElementById('turnstile-container');
            if (container) {
                turnstileWidgetId = (window as any).turnstile.render('#turnstile-container', {
                   //sitekey: '1x00000000000000000000AA', // Clave de prueba que siempre funciona
                    sitekey: '0x4AAAAAABuLJCUfdh4qBZMq', 
                    callback: (token: string) => {
                        turnstileToken = token;
                        errorMessage = ''; // Limpiar errores previos
                    },
                    'error-callback': () => {
                        errorMessage = $translate('Captcha verification error. Please try again.');
                        turnstileToken = '';
                    },
                    theme: 'light'
                });
            }
        }
    }


   // Función para resetear el captcha
    function resetTurnstile() {
        if (typeof window !== 'undefined' && (window as any).turnstile && turnstileWidgetId) {
            (window as any).turnstile.reset(turnstileWidgetId);
            turnstileToken = '';
        }
    }
    
</script>


<svelte:head>
    <script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit"
        async
        defer
    ></script>
</svelte:head>

<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
    <form class="ui form info" on:submit|preventDefault={handleSubmit}>
        <div class="ui segment" style="width: 350px; padding: 1.5rem;">
            <h1 class="ui centered dividing header" style="margin-bottom: 1rem;">{$translate('LOGIN')}</h1>

            <div class="ui basic segment" style="padding: 1rem;">
                <div class="ui fluid left icon input" style="margin-bottom: 8px;">
                    <i class="envelope icon"></i>
                    <input type="text" id="identifier" placeholder={$translate('Email or Phone Number')} bind:value={identifier} required />
                </div>
                
                <div class="ui fluid left icon input" style="margin-bottom: 8px;">
                    <i class="lock icon"></i>
                    <input type="password" id="pin" placeholder={$translate('Password')} bind:value={pin} required />
                </div>
                
                <!-- Mensaje de error -->
                {#if errorMessage}
                    <div class="ui negative message" style="margin: 8px 0; padding: 8px;">
                        <i class="close icon" on:click={() => errorMessage = ''}></i>
                        <div class="header">{$translate('Error')}</div>
                        <p style="margin: 4px 0;">{errorMessage}</p>
                    </div>
                {/if}
                
                <!-- Widget de Turnstile -->
                <div class="ui center aligned basic segment" style="padding: 8px; margin: 8px 0;">
                    <div id="turnstile-container" style="display: flex; justify-content: center;"></div>
                </div>
                
                <button class="ui fluid primary button" type="submit" disabled={isSubmitting} style="margin-top: 8px;">
                    {isSubmitting ? $translate('Signing in...') : $translate('Sign In')}
                </button>
            </div>
        </div>
    </form>
</div>
